import { useState } from 'react';
import { useExpiringBatches, useExpiredBatches } from '../hooks/useBatches';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RequestInputDiscardDialog } from '../components/batches/RequestInputDiscardDialog';
import type { InputBatch } from '../services/inputBatchesApi';

export const BatcheAlertsPage = () => {
  const [daysAhead, setDaysAhead] = useState(30);
  const expiringQuery = useExpiringBatches(daysAhead);
  const expiredQuery = useExpiredBatches();
  const [discardBatch, setDiscardBatch] = useState<InputBatch | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  const handleRequestDiscard = (batch: InputBatch) => {
    setDiscardBatch(batch);
    setDiscardOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Batch Alerts</h1>
        <p className="text-gray-500 mt-1">
          Monitor expiring and expired batches
        </p>
      </div>

      <Tabs defaultValue="expiring" className="w-full">
        <TabsList className="gap-2">
          <TabsTrigger value="expiring" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Expiring Soon ({expiringQuery.data?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="expired" className="gap-2">
            <AlertCircle className="w-4 h-4" />
            Expired ({expiredQuery.data?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="expiring" className="space-y-4">
          <div className="flex gap-2 mb-4">
            <Select
              value={String(daysAhead)}
              onValueChange={(v) => setDaysAhead(parseInt(v))}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Expiring in 7 days</SelectItem>
                <SelectItem value="14">Expiring in 14 days</SelectItem>
                <SelectItem value="30">Expiring in 30 days</SelectItem>
                <SelectItem value="60">Expiring in 60 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {expiringQuery.isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : expiringQuery.data && expiringQuery.data.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Input Name</TableHead>
                    <TableHead>Batch Number</TableHead>
                    <TableHead>Lot Number</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Days Left</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expiringQuery.data.map((batch) => (
                    <TableRow key={batch.id} className="bg-yellow-50 dark:bg-yellow-950/40">
                      <TableCell className="font-medium">
                        {/* You might need to add input name to the response */}
                        Batch
                      </TableCell>
                      <TableCell>{batch.batchNumber}</TableCell>
                      <TableCell>{batch.lotNumber}</TableCell>
                      <TableCell>{batch.receivedQuantity}</TableCell>
                      <TableCell>
                        {new Date(batch.expiryDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {batch.daysUntilExpiry} days
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No batches expiring soon
            </div>
          )}
        </TabsContent>

        <TabsContent value="expired" className="space-y-4">
          {expiredQuery.isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : expiredQuery.data && expiredQuery.data.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Input Name</TableHead>
                    <TableHead>Batch Number</TableHead>
                    <TableHead>Lot Number</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expiredQuery.data.map((batch) => (
                    <TableRow key={batch.id} className="bg-red-50 dark:bg-red-950/40">
                      <TableCell className="font-medium">Batch</TableCell>
                      <TableCell>{batch.batchNumber}</TableCell>
                      <TableCell>{batch.lotNumber}</TableCell>
                      <TableCell>{batch.receivedQuantity}</TableCell>
                      <TableCell>
                        {new Date(batch.expiryDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleRequestDiscard(batch as unknown as InputBatch)}
                        >
                          Request Discard
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No expired batches
            </div>
          )}
        </TabsContent>
      </Tabs>

      {discardBatch && (
        <RequestInputDiscardDialog
          batch={discardBatch}
          open={discardOpen}
          onOpenChange={setDiscardOpen}
          onSuccess={() => {
            setDiscardOpen(false);
            setDiscardBatch(null);
            expiredQuery.refetch();
          }}
        />
      )}
    </div>
  );
};